'use client';

import Cookies from 'js-cookie';
import { createCartExperimentResolver } from './resolver';
import { CART_EXPERIMENT_CONFIG } from './config';
import { EPlatform } from '@/utils/enums/platform-enum';
import { env } from '@/utils/env';
import logger from '@/utils/client-logger';
import type { CartExperimentConfig, CartExperimentStore } from './resolver';
import type { CartExperimentState } from './types';

export type { CartExperimentConfig, CartExperimentStore } from './resolver';

const resolver = createCartExperimentResolver(async (paramName, signal) => {
  const { getAbTestingData } = await import('@/api/public');
  // 解析已超时或因身份/配置变化失效时不再启动请求；signal 同时取消等待 SDK 或已发出的 HTTP。
  if (signal.aborted) throw new Error('Cart experiment resolution cancelled');
  const response = await getAbTestingData({ param_name: paramName }, { signal });

  // 开发日志独立于实验启用条件；部署环境不影响真实分桶接入。
  if (process.env.NODE_ENV === 'development') {
    logger.log('[Cart AB 接口返回]', {
      paramName,
      code: response?.code,
      group: response?.data?.strategy_variable,
    });
  }

  return response;
});

/** US 各环境默认接入真实分桶；页面与优惠策略由接口返回决定，非 US 保持关闭。 */
export function getCartExperimentConfig(): CartExperimentConfig {
  return env.NEXT_PUBLIC_SITE_NAME === 'us' ? CART_EXPERIMENT_CONFIG : { enabled: false };
}

/** 由客户端生命周期/事件调用，禁止 render 中调用；Store 必须来自当前 Provider。 */
export function resolveCartExperiment(
  store: CartExperimentStore,
  config: CartExperimentConfig = { enabled: false },
): Promise<CartExperimentState> {
  return resolver.resolve(store, config);
}

/** Header 与页面共用真实实验结果；URL 参数不能覆盖服务端分桶或关闭实验。 */
export function resolveBrowserCartExperiment(
  store: CartExperimentStore,
): Promise<CartExperimentState> {
  if (typeof window === 'undefined') return Promise.resolve({ status: 'disabled' });
  // 顶栏计价可能按屏幕宽度触发；宽屏 App 也必须遵守 US PC/WAP 的实验范围。
  const deviceType = Number(Cookies.get('deviceType'));
  if (deviceType === EPlatform.IOS || deviceType === EPlatform.ANDROID) {
    return Promise.resolve({ status: 'disabled' });
  }
  return resolver.resolve(store, getCartExperimentConfig());
}

/** 仅供安全的重新初始化；登录/领券、单个消费者卸载均不应调用。 */
export function resetCartExperiment(store: CartExperimentStore): void {
  resolver.reset(store);
}
